import { Checkbox } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '../../hooks/useBoolean';
import { DataItem, updateDailySubContractor } from '../../services/apiSLTP/activities';
import { createDailySubContractor, DailySubContractor, deleteDailySubContractor } from '../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getPriceTypeInfos, stepModes, systemConfigKeyEnum } from '../../utils/enums';
import DraggableCell from '../DraggableCell';
import DroppableCell from '../DroppableCell';
import ManageDailySubContractorModal from '../modals/operations/planning/ManageDailySubContractorModal';
import IconButton from '../shared/IconButton';
import { useSystemConfig } from '../../hooks/useSystemConfig';

interface SubContractorsCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    hiddenPlusIcon?: boolean;
    refetch?: () => void;
}

const SubContractorsCell: React.FC<SubContractorsCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    hiddenPlusIcon = false,
    refetch = () => {},
}) => {
    const { getConfigValue } = useSystemConfig();
    const manageModal = useBoolean(false);
    const [selectedItem, setSelectedItem] = useState<DailySubContractor | null>(null);

    const { t } = useTranslation();
    const itemType = 'SUBCONTRACTORS';

    const handleSubContractorsChange = async (fromLineId: string, fromItemId: string, toLineId: string) => {
        if (fromLineId === toLineId) return;
        try {
            await updateDailySubContractor(fromItemId, { activityId: toLineId });
            refetch();
        } catch (error) {
            console.error('Error updating daily sub contractor :', error);
        }
    };

    const handleDeleteSubContractor = async (id: string, index: number) => {
        try {
            await deleteDailySubContractor(id);
            refetch();
        } catch (error) {
            console.error('Error deleting daily sub contractor:', error);
        }
    };

    const handleCreateDailySubContractor = async (dailySubContractorId: string) => {
        const selectedSubContractor = activity.dailySubContractors.find((value) => value._id === dailySubContractorId);
        if (selectedSubContractor) {
            const response = await createDailySubContractor({
                activityId: activity._id,
                subContractorTypeId: selectedSubContractor.subContractorTypeId._id,
                price: selectedSubContractor.price,
                priceType: selectedSubContractor.priceType,
            });
            refetch();
        }
    };

    return (
        <div className={`${'text-black'}`} style={{ display: 'flex', height: '100%', width: '100%', opacity: disabled ? 0.7 : 1 }}>
            <DroppableCell
                dropId={{ lineId: activity._id }}
                moveCell={(fromLineId, fromItemId, toLineId) => handleSubContractorsChange(fromLineId, fromItemId, toLineId)}
                itemType={itemType}
                disabled={disabled}
            >
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                onClick={() => {
                                    manageModal.setTrue();
                                    setSelectedItem(null);
                                }}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addSubContractor'))}
                            />
                        )}
                    </div>
                    <div className="gap-5px fx-c">
                        {activity.dailySubContractors.map((subContractor, index) => (
                            <div key={`${activityIndex}-${index}`} className="fx-r fx-fg-1 gap-5px fx-items-fs">
                                <DraggableCell
                                    key={`${activityIndex}-${index}`}
                                    dragIds={{ itemId: subContractor._id, lineId: activity._id }}
                                    index={index}
                                    itemType={itemType}
                                    onDelete={() => handleDeleteSubContractor(subContractor._id, index)}
                                    disabled={disabled}
                                    state={subContractor.state}
                                    onDuplicate={() => handleCreateDailySubContractor(subContractor._id)}
                                    onDoubleClick={() => {
                                        manageModal.setTrue();
                                        setSelectedItem(subContractor);
                                    }}
                                >
                                    <div title={subContractor.subContractorTypeId.designation} className="one-line-container">
                                        <div className="fx-r fx-fg-1 fx-justify-sb fx-items-c">
                                            <div className="font-w-700 one-line">{subContractor.subContractorTypeId.designation}</div>
                                        </div>
                                        {activity.stepMode === stepModes.VALIDATE && (
                                            <div className="fx-r fx-fg-1 fx-justify-sb fx-items-c">
                                                <div className="one-line" title={subContractor.subContractorTypeId.codeProC}>
                                                    {subContractor.subContractorTypeId.codeProC}
                                                </div>
                                            </div>
                                        )}
                                        {activity.stepMode === stepModes.VALIDATE &&
                                            typeof subContractor.price === 'number' &&
                                            typeof subContractor.priceType === 'number' && (
                                                <div
                                                    className="one-line font-italic"
                                                    style={{
                                                        color: '#DC4D23',
                                                        textDecorationLine:
                                                            subContractor?.includedInExpenses && activity.includedInExpenses
                                                                ? undefined
                                                                : 'line-through',
                                                    }}
                                                >
                                                    {subContractor.price}{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}/{capitalizeFirstLetter(t(getPriceTypeInfos(subContractor.priceType)))}
                                                </div>
                                            )}
                                    </div>
                                </DraggableCell>
                                {activity.stepMode === stepModes.VALIDATE && (
                                    <div className="h-25px fx-r fx-items-c">
                                        <Checkbox
                                            checked={subContractor.includedInExpenses}
                                            onChange={async (_, checked) => {
                                                await updateDailySubContractor(subContractor._id, { includedInExpenses: checked });
                                                refetch();
                                            }}
                                            color="success"
                                            sx={{ padding: 0 }}
                                            disabled={disabled || !activity.includedInExpenses}
                                            title={capitalizeFirstLetter(t('includedInExpenses'))}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </DroppableCell>
            {manageModal.value && (
                <ManageDailySubContractorModal
                    open={manageModal.value}
                    activity={activity}
                    activityIndex={activityIndex}
                    handleClose={() => manageModal.setFalse()}
                    refetch={refetch}
                    dailySubContractor={selectedItem}
                />
            )}
        </div>
    );
};

export default SubContractorsCell;
